import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tap, map, catchError, finalize } from 'rxjs/operators';
import { GlobalStore, GlobalState, createInitialState } from './global.store';
import {
	flattenStrapiCollection,
	flattenStrapiSingleResponse,
	GlobalSettings,
	StrapiSingleResponse,
	StrapiCollectionResponse,
	Country
} from './global.model';
import { environment } from '../../../../environments/environment';
import { forkJoin, Observable, of } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { scrollToElementId } from '../../utils/dom.utils';
import { Title } from '@angular/platform-browser';
import { groupItemsByParent } from './global.utils';
import qs from 'qs';
import { CMS_LOCALES, setCountryOptions } from './global.data';
import { LocaleCode } from '../../models/locale';
import { StrapiLocale } from './strapi-locale.model';
import { resolveApprovalMode } from './approval-mode.utils';

/**
 * Settings that are owned by the CMS organization-setting and are read-only to the app.
 *
 * The API keeps its own copy of these (synced from the CMS by the `updateSiteConfigs` cron,
 * so it can lag the CMS by up to two hours) and echoes them back in every site-settings
 * response. Letting that copy into the store would overwrite the live CMS value fetched at
 * bootstrap — for `enableFileLibrarySettings` that means the Settings -> File Library page can
 * disappear after saving on it, even though the CMS still has the feature enabled (#10780).
 */
export const CMS_OWNED_SETTING_KEYS: readonly (keyof GlobalSettings)[] = ['enableFileLibrarySettings'];

/**
 * Strip CMS-owned settings from an API-sourced settings object so they can't overwrite the
 * live CMS values held in the store.
 */
export function omitCmsOwnedSettings(settings: GlobalSettings): Partial<GlobalSettings> {
	if (!settings) {
		return {};
	}

	const result: Partial<GlobalSettings> = { ...settings };
	for (const key of CMS_OWNED_SETTING_KEYS) {
		delete result[key];
	}

	return result;
}

@Injectable({ providedIn: 'root' })
export class GlobalService {
	constructor(
		private readonly globalStore: GlobalStore,
		private readonly http: HttpClient,
		private readonly snackBar: MatSnackBar,
		private readonly titleService: Title
	) {}

	get() {
		const authHeaders = {
			Authorization: `Bearer ${environment.cmsToken}`
		};
		let authentication = this.http.get<StrapiSingleResponse<any>>(`${environment.cmsUrl}/api/authentication`, {
			headers: authHeaders
		});

		const query = qs.stringify({
			populate: {
				creativePackageCategories: {
					populate: 'parent'
				},
				paymentMethods: {
					populate: '*'
				},
				products: {
					populate: '*'
				},
				organizationLogo: {
					populate: '*'
				},
				loginPageImage: {
					populate: '*'
				},
				signupPageImage: {
					populate: '*'
				},
				freeTrialRegions: {
					populate: '*'
				}
			}
		});
		let orgSettings = this.http.get<StrapiSingleResponse<any>>(`${environment.cmsUrl}/api/organization-setting?${query}`, {
			headers: authHeaders
		});

		const siteCopyQuery = qs.stringify({
			locale: this.getCmsLocale(),
			populate: {
				heroGetStartedBackground: {
					populate: '*'
				},
				footerLinks: {
					populate: '*'
				},
				headerNavLinks: {
					populate: '*'
				},
				exportProgressMessages: {
					populate: '*'
				},
				freeTrialPurchaseDialogCopy: {
					populate: '*'
				}
			}
		});
		let siteCopy = this.http.get<StrapiSingleResponse<any>>(`${environment.cmsUrl}/api/site-copy?${siteCopyQuery}`, {
			headers: authHeaders
		});

		const locales = this.http.get<StrapiLocale[]>(`${environment.cmsUrl}/api/i18n/locales`, { headers: authHeaders });

		// Fetch countries from Strapi with current locale
		const countriesQuery = qs.stringify({
			sort: ['name:asc'],
			pagination: {
				pageSize: 100
			},
			locale: this.getCmsLocale('en'),
			publicationState: 'live'
		});
		const countries = this.http.get<StrapiCollectionResponse<Country>>(`${environment.cmsUrl}/api/countries?${countriesQuery}`, {
			headers: authHeaders
		});

		// Fetch API site settings (includes fileLibrarySettings)
		// Type matches Site entity which has settings property
		type ApiSiteResponse = { settings?: Partial<GlobalSettings> } | null;
		const apiSiteSettings = this.http.get<ApiSiteResponse>(`${environment.apiUrl}/site/${environment.organizationId}/public`).pipe(
			catchError(() => {
				// If API fails, continue with empty settings
				console.warn('Failed to fetch API site settings, continuing with CMS settings only');
				return of(null as ApiSiteResponse);
			})
		);

		return forkJoin([authentication, orgSettings, siteCopy, locales, countries, apiSiteSettings]).pipe(
			map(([authentication, orgSettings, siteCopy, locales, countriesResponse, apiSite]) => {
				// Process countries and update the global COUNTRY_OPTIONS cache
				const countriesData = flattenStrapiCollection(countriesResponse);
				setCountryOptions(countriesData);

				const mergedValues = {
					...authentication?.data?.attributes,
					...this.convertStrapiOrgSettings(orgSettings),
					locales
				};

				const siteCopyValues = siteCopy?.data?.attributes || {};
				for (const [key, value] of Object.entries(siteCopyValues)) {
					if (mergedValues[key] && !value) {
						// don't override the value if it's already set and siteCopy has it undefined
					} else {
						mergedValues[key] = value;
					}
				}

				// Merge API site settings (fileLibrarySettings etc.)
				// API settings take precedence for fields that exist in the API
				const apiSettings = apiSite?.settings;
				mergedValues['exportShareLinksEnabled'] = apiSettings?.exportShareLinksEnabled === true;
				if (apiSettings) {
					if (apiSettings.fileLibrarySettings) {
						// `fileLibrarySettings.enableFileLibrary` is the site-level runtime toggle owned by
						// Settings -> File Library. It must never be written over the top-level
						// `enableFileLibrarySettings`, which is CMS-owned (see CMS_OWNED_SETTING_KEYS):
						// deriving one from the other made turning the library off hide the settings page
						// that owns the toggle, leaving no way to turn it back on (#10780).
						// This bag also carries the app-owned `startingPage`.
						mergedValues['fileLibrarySettings'] = apiSettings.fileLibrarySettings;
					}
					if (apiSettings.enableBusinessAdminReporting !== undefined) {
						mergedValues['enableBusinessAdminReporting'] = apiSettings.enableBusinessAdminReporting;
					}
				}

				return mergedValues;
			}),
			tap((settings: GlobalSettings) =>
				this.globalStore.update({
					settings
				})
			)
		);
	}

	setState(state: GlobalState['state'], scrollToTop = true) {
		if (scrollToTop && state !== this.globalStore.getValue().state) {
			scrollToElementId('top');
		}

		this.globalStore.update({
			state
		});
	}

	setPages(pages: GlobalState['pages']) {
		this.globalStore.update({
			pages
		});
	}

	resetPages() {
		this.globalStore.update({
			pages: createInitialState().pages
		});
	}

	nextState() {
		scrollToElementId('top');

		// Find the current page and then iterate up
		const currentState = this.globalStore.getValue().state;

		this.globalStore.getValue().pages.forEach((page, i) => {
			if (page.id === currentState) {
				// Found a match, lets go to the next page though
				if (i >= this.globalStore.getValue().pages.length - 1) {
					// Should we go back to the start?
				} else {
					this.globalStore.update({
						state: this.globalStore.getValue().pages[i + 1].id
					});
				}
			}
		});
	}

	generateVideoPreview() {
		this.queueVideoProcessing(true);
	}

	generateVideo() {
		this.queueVideoProcessing(false);
	}

	queueVideoProcessing(watermark: boolean) {
		// Get the Correct story segment clips and any extra properties
		// Get the active music track
		// Get the video format
	}

	/**
	 * Set if the active project has
	 */
	dirtyTick() {
		this.globalStore.update({
			dirtyTick: new Date().toISOString()
		});
	}

	resetDirtyTick() {
		this.globalStore.update({
			dirtyTick: undefined
		});
	}

	setAudioPlaying(id: string) {
		this.globalStore.update({
			audioPlaying: id
		});
	}

	stopAudio() {
		this.globalStore.update({
			audioPlaying: undefined
		});
	}

	startAudioPreviewPlaying() {
		this.globalStore.update({
			audioPreviewPlaying: true
		});
	}

	stopAudioPreviewPlaying() {
		this.globalStore.update({
			audioPreviewPlaying: false
		});
	}

	/**
	 * Toggles the muted state of the editor video in the global store.
	 * @param state - Optional new value for the muted state.
	 * If not provided, the muted state is flipped from its current value.
	 */
	toggleEditorVideoMuted(state?: boolean) {
		this.globalStore.update({
			editorVideoMuted: state || !this.globalStore.getValue().editorVideoMuted
		});
	}

	/**
	 * Set an Admin Mode that triggers some debug UI on the site.
	 * Search adminMode to find all of the places this applies.
	 */
	setAdminMode(state?: boolean) {
		const newState = state ?? !this.globalStore.getValue().adminMode;

		localStorage.setItem('adminMode', String(newState));

		this.globalStore.update({
			adminMode: newState
		});
	}

	/**
	 * Open a snack bar presenting the error message to the user.
	 * @param message Override with a custom message
	 */
	triggerSaveMessage(message?: string) {
		this.snackBar.open(message || $localize`:Save Message@@saveMessageSaveSuccessfulText:Save Successful.`, undefined, {
			verticalPosition: 'bottom',
			horizontalPosition: 'right',
			duration: 2000,
			panelClass: 'success'
		});
	}

	/**
	 * Open a snack bar presenting the success message to the user.
	 * @param message Override with a custom message
	 * @param config Override default config
	 * @param action
	 */
	triggerSuccessMessage(message?: string, config?: any, action?: { label: string; callback: () => void }) {
		let snack = this.snackBar.open(message || 'Finished.', action?.label, {
			verticalPosition: 'bottom',
			horizontalPosition: 'center',
			duration: 4000,
			panelClass: 'success',
			...config
		});

		// If an action callback, listen for that event.
		if (action?.callback) {
			snack.onAction().subscribe(() => {
				if (action?.callback) {
					action.callback();
				}
			});
		}
	}

	/**
	 * Open a snack bar presenting the error message to the user.
	 * @param err The error response object
	 * @param message Override with a custom message
	 * @param config Override default config
	 */
	triggerErrorMessage(err: HttpErrorResponse, message?: string, config?: any) {
		this.snackBar.open(
			err?.error?.message ||
				err?.message ||
				message ||
				$localize`:Error Message@@errorMessageCompletingTaskErrorText:There was an error completing this task.`,
			undefined,
			{
				duration: 4000,
				panelClass: 'danger',
				...config
			}
		);
	}

	/**
	 * Open a snack bar presenting the error message to the user.
	 * @param message custom message
	 * @param className custom styles to apply
	 * @param config configuration object for snackbar
	 */
	triggerCustomMessage(message?: string, className?: string, config: any = {}) {
		this.snackBar.open(
			message || $localize`:Error Message@@errorMessageCompletingTaskErrorText:There was an error completing this task.`,
			undefined,
			{
				duration: 4000,
				panelClass: className,
				...config
			}
		);
	}

	/**
	 * Set the Browser title bar message.
	 */
	setTitle(title: string) {
		this.titleService.setTitle(title + ' | ' + this.globalStore.getValue()?.settings?.organizationName);
	}

	/**
	 * Copy the provided text to the clipboard.
	 */
	copyToClipboard(text: string) {
		let textArea = document.createElement('textarea');
		textArea.value = text;
		document.body.appendChild(textArea);
		textArea.select();
		document.execCommand('Copy');
		textArea.remove();

		this.triggerSuccessMessage($localize`:Success Message@@copyToClipboardSuccessMessageText:Copied to clipboard.`);
	}

	/**
	 * Convert the Strapi response into usable object.
	 */
	convertStrapiOrgSettings(orgSettings: StrapiSingleResponse<any>) {
		if (!orgSettings?.data?.attributes) {
			return {};
		}
		const settings = orgSettings.data.attributes;

		// Convert the creative package categories into a usable object.
		let creativePackageCategories = flattenStrapiCollection(settings?.creativePackageCategories).map((category: any) => ({
			...category,
			parent: flattenStrapiSingleResponse(category?.parent)
		}));

		return {
			...settings,
			approvalMode: resolveApprovalMode(settings.approvalMode),
			creativePackageCategories: groupItemsByParent(creativePackageCategories),
			paymentMethodsRequired: settings?.paymentMethodsRequired !== false
		};
	}

	getLocale() {
		let code = localStorage.getItem('localeCode');
		const native = navigator.language;

		if (!code) {
			code = native;
			if (code.includes('CN')) {
				return LocaleCode.zhCN;
			}
			if (code.includes('CA')) {
				return LocaleCode.frCA;
			}
		}

		if (!Object.values(LocaleCode).includes(code as LocaleCode)) {
			return LocaleCode.enUS;
		}

		return code;
	}

	getCmsLocale(fallback?: string) {
		const code = this.getLocale();

		return CMS_LOCALES[code] || fallback;
	}

	updateSiteConfigs(): Observable<any> {
		return this.http.put<any>(`${environment.apiUrl}/site/updateSiteConfigs`, {}).pipe(
			catchError(err => {
				this.triggerErrorMessage(undefined, $localize`:Error Message@@errorMessageCMSSyncCompletedText:Error syncing CMS.`);
				throw err;
			}),
			finalize(() => this.triggerSuccessMessage($localize`:Success Message@@successMessageCMSSyncCompletedText:CMS Sync completed.`))
		);
	}

	/**
	 * Update site settings and refresh the GlobalStore cache.
	 * @param siteId The site ID to update
	 * @param settings Partial settings object to merge
	 * @returns Observable of the updated settings
	 */
	updateSiteSettings(siteId: string, settings: Partial<GlobalSettings>): Observable<GlobalSettings> {
		// API returns full Site object, we extract settings from it
		return this.http.patch<{ settings: GlobalSettings }>(`${environment.apiUrl}/site/${siteId}/settings`, settings).pipe(
			map(site => site.settings),
			tap(updatedSettings => {
				this.globalStore.update(state => ({
					...state,
					settings: { ...state.settings, ...omitCmsOwnedSettings(updatedSettings) }
				}));
			})
		);
	}
}
